import { useEffect, useState } from 'react';
import axios from 'axios';

// The category ontology (English canonical name + French name), fetched once and
// cached at module scope so any tab can localize a detected category label
// without each refetching. Read-only: the Parts DB tab owns editing the ontology.
// `kind` is what a category IS: a part of one product, generic hardware, or a tool. Empty
// means "ask my parent", which is what all but the three roots say. See groupOfCategory.
export type CategoryKind = 'component' | 'hardware' | 'tool';
export type CategoryDef = {
  name: string; nameFr: string; parent?: string; kind?: CategoryKind | '';
};

let cache: CategoryDef[] | null = null;
let inflight: Promise<CategoryDef[]> | null = null;

function load(): Promise<CategoryDef[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = axios
      .get<{ categories: CategoryDef[] }>('/api/notice/categories')
      .then(({ data }) => (cache = data.categories || []))
      .catch(() => (cache = []))
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

// Drop the cached list so the next read refetches — call after the Parts DB tab
// adds / renames / deletes a category, so the localized labels elsewhere update.
export function invalidateCategories() {
  cache = null;
}

// The ontology as it stands right now, for the callers that are NOT components and cannot
// hold a subscription: a sort comparator, a store's derived value, a plain function asked
// "is this part hardware".
//
// Those callers used to carry a hardcoded map of category names instead, which is how they
// went stale. Reading the cache is the same answer one render later — every screen that
// asks has already mounted a useCategories somewhere above it — and an empty list before
// the first fetch resolves everything to the default, which is what the map did anyway.
export const getCategories = (): CategoryDef[] => cache ?? [];

// WHETHER THAT ANSWER IS THE ONTOLOGY'S OR THE DEFAULT'S. `getCategories` cannot say: it
// returns [] both for "not fetched yet" and for "fetched, and empty", and everything reading
// it resolves [] to the default kind — component.
//
// Silence is a fine answer for a sort order or a folder heading. It is the wrong one for a
// caller WRITING the kinds into a prompt: labelling every screw a component part is not a
// slightly worse label, it is a false one, and the four kinds of sub-step are counted in
// component and hardware parts. That caller skips the labels rather than print them wrong —
// see partKindWord's use in stepsRun.
export const categoriesLoaded = (): boolean => cache !== null;

// THE ONTOLOGY, WAITED FOR — for a caller that must not run without it.
//
// `useCategories` is the answer for anything that renders: it subscribes, and one render
// later the labels are right. A RUN is not a render. It builds a prompt once, sends it, and
// pays for the reply, so "one render later" is a second call and a wrong answer already
// bought. Awaiting this before the first prompt is built is the difference between a screw
// described as a screw and a screw offered as a plank to drill into.
//
// It is `load` itself and not a copy of it: the same module cache, the same in-flight promise,
// so a run starting while a component is already fetching joins that fetch rather than making
// a second.
export const ensureCategories = (): Promise<CategoryDef[]> => load();


// Subscribe a component to the category list (fetched once, module-cached). The
// shared cache dedupes the fetch across every tab that reads it.
export function useCategories(): CategoryDef[] {
  const [cats, setCats] = useState<CategoryDef[]>(cache ?? []);
  useEffect(() => {
    let alive = true;
    void load().then((c) => {
      if (alive) setCats(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  return cats;
}

// The French name the ontology gives this category, or '' if it gives none — either
// because the category is not in the ontology at all (a model is free to answer with
// anything) or because its row was never given one.
const frenchName = (cats: CategoryDef[], name: string): string =>
  cats.find((c) => c.name.toLowerCase() === name.toLowerCase())?.nameFr?.trim() || '';

// Map a detected English category name to its display label in the given
// language: the French name when the app is in FR and one exists, else the
// English name (which is also what EN always shows).
//
// For the places where the label IS the thing — the Parts DB folders, which are named
// by their category and are also where a missing French name gets typed in. A folder
// with no name would be unopenable and unfixable.
export function localizeCategory(
  cats: CategoryDef[],
  name: string,
  language: 'en' | 'fr',
): string {
  const v = (name || '').trim();
  if (!v || language !== 'fr') return v;
  return frenchName(cats, v) || v;
}

// The same, except a category with no French name shows as NOTHING rather than as its
// English key.
//
// For the manual's tables, where the category is one field among many and the English
// key is the model's answer leaking through a French page. Blank is not a loss of
// information there — the cell is one column of a table that says what the part is
// twenty other ways — and it is the only thing that makes a missing translation
// visible, which is what gets it added to the ontology.
export function localizeCategoryOrBlank(
  cats: CategoryDef[],
  name: string,
  language: 'en' | 'fr',
): string {
  const v = (name || '').trim();
  if (!v || language !== 'fr') return v;
  return frenchName(cats, v);
}
